import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function Equipment() {
  const [list, setList] = useState([]);
  const [kpiMap, setKpiMap] = useState({});
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [typeTab, setTypeTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", name: "", code: "", category: "", equipment_type: "fixed", criticality: "medium", oem: "", model: "", serial_number: "", asset_id: "", commission_date: "", capacity: "", pm_frequency: "", warranty_expiry: "", vendor: "", expected_life_years: 0 });

  const [ops, setOps] = useState([]);
  const [workorders, setWorkorders] = useState([]);
  const [opsOpen, setOpsOpen] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const blankOp = { project_id: "", equipment_id: "", date: todayStr, shift: "A", shift_hours: 8, running_hours: "", breakdown_hours: 0, idle_hours: 0, planned_maintenance_hours: 0, operator: "", work_order_id: "", remarks: "" };
  const [opForm, setOpForm] = useState(blankOp);

  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); }); load(); }, []);
  const load = () => {
    api.get("/equipment").then((r) => setList(r.data));
    api.get("/equipment/kpis").then((r) => {
      setSummary(r.data);
      setKpiMap(Object.fromEntries((r.data.equipment || []).map((e) => [e.id, e])));
    }).catch(() => {});
    api.get("/equipment/operations").then((r) => setOps(r.data)).catch(() => {});
    api.get("/workorders").then((r) => setWorkorders(r.data)).catch(() => {});
  };

  const submit = async () => {
    try { await api.post("/equipment", { ...form, expected_life_years: parseFloat(form.expected_life_years) || 0 }); toast.success("Equipment added"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const opTotal = ["running_hours", "breakdown_hours", "idle_hours", "planned_maintenance_hours"]
    .reduce((s, k) => s + (parseFloat(opForm[k]) || 0), 0);

  const openOpsDialog = () => {
    const eq = list[0];
    setOpForm({ ...blankOp, project_id: eq?.project_id || projects[0]?.id || "", equipment_id: eq?.id || "" });
    setOpsOpen(true);
  };

  const submitOp = async () => {
    const eq = list.find((e) => e.id === opForm.equipment_id);
    try {
      await api.post("/equipment/operations", {
        ...opForm,
        project_id: eq?.project_id || opForm.project_id,
        shift_hours: parseFloat(opForm.shift_hours) || 8,
        running_hours: parseFloat(opForm.running_hours) || 0,
        breakdown_hours: parseFloat(opForm.breakdown_hours) || 0,
        idle_hours: parseFloat(opForm.idle_hours) || 0,
        planned_maintenance_hours: parseFloat(opForm.planned_maintenance_hours) || 0,
        work_order_id: opForm.work_order_id || null,
      });
      toast.success("Operations log recorded");
      setOpsOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const eqCode = (id) => list.find((e) => e.id === id)?.code || "—";
  const woNumber = (id) => workorders.find((w) => w.id === id)?.wo_number;

  const filt = list.filter((e) =>
    (typeTab === "all" || (e.equipment_type || "fixed") === typeTab) &&
    (!q || `${e.name} ${e.code} ${e.oem} ${e.model}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <AppLayout title="Equipment Master" subtitle="Asset register with availability, MTBF & MTTR">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5">
        <Card className="p-4" data-testid="equipment-kpi-fleet"><div className="label-cap text-slate-500">Fleet Availability</div><div className="text-2xl font-bold text-emerald-600 mt-1">{summary?.fleet_availability ?? "—"}%</div></Card>
        <Card className="p-4"><div className="label-cap text-slate-500">Fixed Equipment</div><div className="text-2xl font-bold mt-1">{summary?.fixed_count ?? "—"}</div></Card>
        <Card className="p-4"><div className="label-cap text-slate-500">Mobile Equipment</div><div className="text-2xl font-bold mt-1">{summary?.mobile_count ?? "—"}</div></Card>
        <Card className="p-4"><div className="label-cap text-slate-500">Operational</div><div className="text-2xl font-bold text-sky-600 mt-1">{list.filter((e) => e.status === "operational").length}</div></Card>
        <Card className="p-4"><div className="label-cap text-slate-500">Down / Maint.</div><div className="text-2xl font-bold text-red-600 mt-1">{list.filter((e) => e.status !== "operational").length}</div></Card>
      </div>
      <div className="flex gap-2 mb-3">
        {["all", "fixed", "mobile"].map((t) => (
          <button key={t} onClick={() => setTypeTab(t)} data-testid={`equipment-tab-${t}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${typeTab === t ? "bg-sky-900 text-white border-sky-900" : "bg-white text-slate-600"}`}>
            {t === "all" ? "All" : t === "fixed" ? "Fixed Equipment" : "Mobile Equipment"}
          </button>
        ))}
      </div>
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="Add Equipment"
        addTestId="equipment-add-button" searchTestId="equipment-search-input" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Criticality</TableHead>
              <TableHead>OEM</TableHead>
              <TableHead className="text-right">Cum. Run Hrs</TableHead>
              <TableHead className="text-right">Run Hrs (Mth)</TableHead>
              <TableHead className="text-right">Avail %</TableHead>
              <TableHead className="text-right">Util %</TableHead>
              <TableHead className="text-right">MTBF (h)</TableHead>
              <TableHead className="text-right">MTTR (h)</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((e) => {
              const k = kpiMap[e.id] || {};
              return (
              <TableRow key={e.id} data-testid={`equipment-row-${e.code}`}>
                <TableCell className="font-mono font-semibold">{e.code}</TableCell>
                <TableCell>{e.name}</TableCell>
                <TableCell><span className={`text-[10px] px-2 py-0.5 rounded-full ${(e.equipment_type||"fixed")==="mobile"?"bg-indigo-100 text-indigo-700":"bg-slate-100 text-slate-600"}`}>{e.equipment_type || "fixed"}</span></TableCell>
                <TableCell><StatusBadge status={e.criticality || "medium"} /></TableCell>
                <TableCell>{e.oem || "—"}</TableCell>
                <TableCell className="text-right font-mono">{e.running_hours?.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono" data-testid={`equipment-runmonth-${e.code}`}>{k.has_logs ? k.running_month : "—"}</TableCell>
                <TableCell className="text-right font-mono">{k.has_logs ? k.availability : "—"}</TableCell>
                <TableCell className="text-right font-mono">{k.has_logs ? k.utilization : "—"}</TableCell>
                <TableCell className="text-right font-mono">{k.has_logs ? k.mtbf : "—"}</TableCell>
                <TableCell className="text-right font-mono">{k.has_logs ? k.mttr : "—"}</TableCell>
                <TableCell><StatusBadge status={e.status} /></TableCell>
              </TableRow>
            );})}
            {!filt.length && <TableRow><TableCell colSpan={12} className="text-center py-12 text-slate-400">No equipment</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* ----- Equipment Operations Log ----- */}
      <div className="flex items-center justify-between mt-8 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Equipment Operations Log</h3>
          <p className="text-sm text-slate-500">Shift-wise running, breakdown, idle &amp; planned-maintenance hours — drives Availability, Utilization, MTBF &amp; MTTR.</p>
        </div>
        <Button onClick={openOpsDialog} className="bg-sky-900 hover:bg-sky-950" data-testid="ops-log-button">Log Operating Hours</Button>
      </div>
      <Card className="overflow-x-auto" data-testid="ops-log-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Shift</TableHead><TableHead>Equipment</TableHead>
              <TableHead className="text-right">Running</TableHead>
              <TableHead className="text-right">Breakdown</TableHead>
              <TableHead className="text-right">Idle</TableHead>
              <TableHead className="text-right">Pl. Maint.</TableHead>
              <TableHead>Operator</TableHead><TableHead>Job Order</TableHead><TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ops.slice(0, 50).map((o) => (
              <TableRow key={o.id} data-testid={`ops-row-${o.id}`}>
                <TableCell className="font-mono">{o.date}</TableCell>
                <TableCell>{o.shift}</TableCell>
                <TableCell className="font-mono">{eqCode(o.equipment_id)}</TableCell>
                <TableCell className="text-right font-mono text-emerald-700">{o.running_hours}</TableCell>
                <TableCell className="text-right font-mono text-red-600">{o.breakdown_hours}</TableCell>
                <TableCell className="text-right font-mono text-slate-500">{o.idle_hours}</TableCell>
                <TableCell className="text-right font-mono text-amber-600">{o.planned_maintenance_hours}</TableCell>
                <TableCell>{o.operator || "—"}</TableCell>
                <TableCell className="font-mono">{woNumber(o.work_order_id) || "—"}</TableCell>
                <TableCell className="text-slate-500">{o.remarks || "—"}</TableCell>
              </TableRow>
            ))}
            {!ops.length && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400" data-testid="ops-empty">No operations logged yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={opsOpen} onOpenChange={setOpsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ops-log-dialog">
          <DialogHeader><DialogTitle>Log Operating Hours</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-cap">Equipment</Label>
              <Select value={opForm.equipment_id} onValueChange={(v) => setOpForm({ ...opForm, equipment_id: v })}>
                <SelectTrigger data-testid="ops-equipment-input"><SelectValue placeholder="Select equipment" /></SelectTrigger>
                <SelectContent>{list.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Date</Label><Input type="date" max={todayStr} data-testid="ops-date-input" value={opForm.date} onChange={(e) => setOpForm({ ...opForm, date: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Shift</Label>
              <Select value={opForm.shift} onValueChange={(v) => setOpForm({ ...opForm, shift: v })}>
                <SelectTrigger data-testid="ops-shift-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["A", "B", "C"].map((s) => <SelectItem key={s} value={s}>Shift {s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Shift Hours</Label><Input type="number" data-testid="ops-shifthours-input" value={opForm.shift_hours} onChange={(e) => setOpForm({ ...opForm, shift_hours: e.target.value })} /></div>
            <div><Label className="label-cap">Running Hours</Label><Input type="number" data-testid="ops-running-input" value={opForm.running_hours} onChange={(e) => setOpForm({ ...opForm, running_hours: e.target.value })} /></div>
            <div><Label className="label-cap">Breakdown Hours</Label><Input type="number" data-testid="ops-breakdown-input" value={opForm.breakdown_hours} onChange={(e) => setOpForm({ ...opForm, breakdown_hours: e.target.value })} /></div>
            <div><Label className="label-cap">Idle Hours</Label><Input type="number" data-testid="ops-idle-input" value={opForm.idle_hours} onChange={(e) => setOpForm({ ...opForm, idle_hours: e.target.value })} /></div>
            <div><Label className="label-cap">Planned Maintenance Hours</Label><Input type="number" data-testid="ops-pm-input" value={opForm.planned_maintenance_hours} onChange={(e) => setOpForm({ ...opForm, planned_maintenance_hours: e.target.value })} /></div>
            <div><Label className="label-cap">Operator</Label><Input data-testid="ops-operator-input" value={opForm.operator} onChange={(e) => setOpForm({ ...opForm, operator: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Linked Job Order</Label>
              <Select value={opForm.work_order_id || "none"} onValueChange={(v) => setOpForm({ ...opForm, work_order_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ops-joborder-input"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {workorders.filter((w) => !opForm.equipment_id || w.equipment_id === opForm.equipment_id).map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.wo_number} — {w.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-cap">Remarks</Label><Input data-testid="ops-remarks-input" value={opForm.remarks} onChange={(e) => setOpForm({ ...opForm, remarks: e.target.value })} /></div>
            <div className="col-span-2 text-sm" data-testid="ops-total-hint">
              Total logged: <span className={`font-mono font-semibold ${opTotal > (parseFloat(opForm.shift_hours) || 8) + 1e-6 ? "text-red-600" : "text-emerald-600"}`}>{opTotal.toFixed(1)} h</span>
              <span className="text-slate-400"> / {parseFloat(opForm.shift_hours) || 8} h shift</span>
            </div>
          </div>
          <DialogFooter><Button onClick={submitOp} disabled={!opForm.equipment_id || opTotal > (parseFloat(opForm.shift_hours) || 8) + 1e-6} className="bg-sky-900 hover:bg-sky-950" data-testid="ops-submit-button">Save Log</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="equipment-create-dialog">
          <DialogHeader><DialogTitle>Add Equipment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-cap">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="equipment-project-input"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Code</Label><Input data-testid="equipment-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
            <div><Label className="label-cap">Name</Label><Input data-testid="equipment-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Type</Label>
              <Select value={form.equipment_type} onValueChange={(v) => setForm({ ...form, equipment_type: v })}>
                <SelectTrigger data-testid="equipment-type-input"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="fixed">Fixed</SelectItem><SelectItem value="mobile">Mobile</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-cap">Criticality</Label>
              <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
                <SelectTrigger data-testid="equipment-criticality-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["critical", "high", "medium", "low"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Category</Label><Input data-testid="equipment-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label className="label-cap">OEM</Label><Input data-testid="equipment-oem-input" value={form.oem} onChange={(e) => setForm({ ...form, oem: e.target.value })} /></div>
            <div><Label className="label-cap">Model</Label><Input data-testid="equipment-model-input" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><Label className="label-cap">Serial #</Label><Input data-testid="equipment-serial-input" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
            <div><Label className="label-cap">Asset ID</Label><Input data-testid="equipment-assetid-input" value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })} /></div>
            <div><Label className="label-cap">Commission Date</Label><Input type="date" data-testid="equipment-commission-input" value={form.commission_date} onChange={(e) => setForm({ ...form, commission_date: e.target.value })} /></div>
            <div><Label className="label-cap">Capacity</Label><Input data-testid="equipment-capacity-input" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
            <div><Label className="label-cap">PM Frequency</Label><Input data-testid="equipment-pmfreq-input" value={form.pm_frequency} onChange={(e) => setForm({ ...form, pm_frequency: e.target.value })} /></div>
            <div><Label className="label-cap">Warranty Expiry</Label><Input type="date" data-testid="equipment-warranty-input" value={form.warranty_expiry} onChange={(e) => setForm({ ...form, warranty_expiry: e.target.value })} /></div>
            <div><Label className="label-cap">Vendor</Label><Input data-testid="equipment-vendor-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
            <div><Label className="label-cap">Expected Life (yrs)</Label><Input type="number" data-testid="equipment-life-input" value={form.expected_life_years} onChange={(e) => setForm({ ...form, expected_life_years: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="equipment-submit-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
